/*
 * The dispute file (owner, 2026-10-31): everything that proves a charge was right, for one client and one invoice, kept
 * out of sight (client page, More, Dispute file) until Stripe opens a dispute. Built by apps/api billing/dispute-file.ts.
 */
import { z } from "zod";

const signedPolicy = z.object({ key: z.string(), title: z.string(), text: z.string() });

/** Stripe's dispute reasons in plain words, and what proof wins each (research-win-disputes). */
export const DISPUTE_REASONS: Record<string, { words: string; proof: string }> = {
  fraudulent: {
    words: "says they did not make this charge",
    proof: "Show the client OK'd it with the signature, the card they saved and past charges they never disputed.",
  },
  unrecognized: {
    words: "does not know this charge",
    proof: "Show who they are to us with the signature, the visits and past charges they paid.",
  },
  product_not_received: {
    words: "says the work was never done",
    proof: "Show the visit happened with times, crew at the address, photos and the texts sent that day.",
  },
  product_unacceptable: {
    words: "says the work was not good",
    proof: "Show the guarantee they signed, when they first told us, our offer to fix it, and the photos.",
  },
  subscription_canceled: {
    words: "says they canceled",
    proof: "Show the cancellation policy they signed and that no cancel came before the visit.",
  },
  credit_not_processed: {
    words: "says a refund was owed",
    proof: "Show the guarantee is a free fix, not a refund, and any refund already given.",
  },
  duplicate: {
    words: "says they were charged twice",
    proof: "Show each charge has its own visit, date and invoice.",
  },
  general: { words: "disputed the charge", proof: "Show the signed agreement, the visit and the messages." },
};

export function disputeReason(reason: string): { words: string; proof: string } {
  return DISPUTE_REASONS[reason] ?? DISPUTE_REASONS.general!;
}

/** True while Stripe still takes proof. */
export function disputeOpen(status: string): boolean {
  return status === "needs_response" || status === "warning_needs_response";
}

export const disputeRow = z.object({
  id: z.uuid(),
  stripeDisputeId: z.string(),
  invoiceId: z.uuid().nullable(),
  reason: z.string(),
  status: z.string(),
  amountCents: z.number().int(),
  dueBy: z.string().nullable(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  sentByName: z.string().nullable(),
  note: z.string().nullable(),
});
export type DisputeRow = z.infer<typeof disputeRow>;

export const disputeVisit = z.object({
  id: z.uuid(),
  date: z.string(),
  startTime: z.string().nullable(),
  status: z.string(),
  completedAt: z.string().nullable(),
  minutes: z.number().int(),
  address: z.string().nullable(),
  crew: z.array(
    z.object({
      name: z.string(),
      clockIn: z.string().nullable(),
      clockOut: z.string().nullable(),
      /** How far the phone was from this home when they clocked in or out, in feet, when both are known. */
      inFeet: z.number().int().nullable(),
      outFeet: z.number().int().nullable(),
    }),
  ),
  /** Live map points within 300 feet of the home on the visit day: first and last time seen there. */
  seenAt: z.object({ from: z.string(), to: z.string() }).nullable(),
  photos: z.array(z.object({ fileId: z.uuid(), kind: z.string(), room: z.string().nullable(), takenAt: z.string() })),
  checklistDone: z.number().int(),
});
export type DisputeVisit = z.infer<typeof disputeVisit>;

export const disputeFile = z.object({
  client: z.object({
    id: z.uuid(),
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address: z.string().nullable(),
    since: z.string(),
    card: z.string().nullable(),
    cardSavedAt: z.string().nullable(),
    /** Typed in by the office while the client read it out (Take card by phone), and by whom. Not the client's signature. */
    cardByPhone: z.boolean(),
    cardTakenBy: z.string().nullable(),
    autoChargeAgreedOn: z.string().nullable(),
  }),
  /** The client's invoices with card payments, newest first, to pick from. */
  invoices: z.array(
    z.object({
      id: z.uuid(),
      number: z.number().int().nullable(),
      totalCents: z.number().int(),
      paidAt: z.string().nullable(),
      disputed: z.boolean(),
    }),
  ),
  invoice: z
    .object({
      id: z.uuid(),
      number: z.number().int().nullable(),
      issuedOn: z.string().nullable(),
      sentAt: z.string().nullable(),
      viewedAt: z.string().nullable(),
      paidAt: z.string().nullable(),
      totalCents: z.number().int(),
      lines: z.array(z.object({ description: z.string(), amountCents: z.number().int() })),
    })
    .nullable(),
  dispute: disputeRow.nullable(),
  /** The signed quote or job confirm behind the work. */
  signature: z
    .object({
      quoteId: z.uuid(),
      quoteNumber: z.number().int().nullable(),
      name: z.string(),
      at: z.string(),
      ip: z.string().nullable(),
      device: z.string().nullable(),
      drawing: z.boolean(),
      /** The words shown above the box. Empty for signatures from before policies were saved. */
      policies: z.array(signedPolicy),
      cardWords: z.string(),
    })
    .nullable(),
  /** Jobs on this invoice the office marked confirmed by hand (the client said yes another way), and by whom. */
  confirmedByHand: z.array(z.object({ jobNumber: z.number().int().nullable(), by: z.string().nullable(), at: z.string() })),
  visits: z.array(disputeVisit),
  payments: z.array(
    z.object({
      at: z.string(),
      amountCents: z.number().int(),
      method: z.string(),
      card: z.string().nullable(),
      status: z.string(),
      chargeId: z.string().nullable(),
      thisOne: z.boolean(),
    }),
  ),
  /** Card charges before this invoice the client paid and never disputed. */
  pastPaid: z.object({ count: z.number().int(), totalCents: z.number().int(), firstAt: z.string().nullable() }),
  messages: z.array(z.object({ at: z.string(), from: z.enum(["us", "client"]), channel: z.string(), body: z.string() })),
  complaints: z.array(
    z.object({
      at: z.string(),
      what: z.string(),
      status: z.string(),
      fix: z.string().nullable(),
      /** Hours after the visit it was about (or the last visit before it). */
      hoursAfter: z.number().int().nullable(),
      inWindow: z.boolean().nullable(),
    }),
  ),
  ratings: z.array(z.object({ at: z.string(), stars: z.number().int(), note: z.string().nullable() })),
  consent: z.array(z.object({ at: z.string(), kind: z.string(), words: z.string() })),
  complaintHours: z.number().int(),
  /** The draft letter to the bank: facts first, in time order. The owner can change it before sending. */
  summary: z.string(),
  /** Points the file is weak on, said plainly (no signature, no photos, a complaint inside the window). */
  gaps: z.array(z.string()),
});
export type DisputeFile = z.infer<typeof disputeFile>;

export const disputeSendBody = z.object({
  invoiceId: z.uuid(),
  /** The letter to the bank, as the owner left it. */
  note: z.string().trim().min(20).max(6000),
});
export type DisputeSendBody = z.infer<typeof disputeSendBody>;
